import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { fetchAllSnapshots, generateCaption } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Sparkle, Copy, InstagramLogo, PlayCircle } from "@phosphor-icons/react";
import { ndviStatus, yieldStatus, toneColor } from "@/lib/laymen";
import RegionSelect from "@/components/RegionSelect";

const FORMATS = [
  { id: "instagram", label: "Feed-Post · 1080×1080", w: 1080, h: 1080 },
  { id: "story", label: "Story · 1080×1920", w: 1080, h: 1920 },
];

const MOODS = [
  { id: "meadow", label: "Wiese", from: "rgba(0,250,154,0.12)", to: "#B9CFA3", accent: "#00FA9A", text: "#1A2E15", chip: "#FFFFFF", chipText: "#1A2E15" },
  { id: "sunrise", label: "Sonnenaufgang", from: "rgba(255,194,75,0.12)", to: "#F0B27A", accent: "#FF7A45", text: "#3A1C10", chip: "#171310", chipText: "#3A1C10" },
  { id: "ink", label: "Tinte", from: "#1E2B22", to: "#3F5A2B", accent: "rgba(0,250,154,0.12)", text: "#121212", chip: "#121212", chipText: "#1A2E15" },
  { id: "lake", label: "See", from: "#D6E4EC", to: "#7FA7BD", accent: "#1C4257", text: "#0E2230", chip: "#121212", chipText: "#0E2230" },
];

export default function ContentStudio() {
  const [data, setData] = useState(null);
  const [regionId, setRegionId] = useState("");
  const [formatId, setFormatId] = useState("instagram");
  const [moodId, setMoodId] = useState("meadow");
  const [caption, setCaption] = useState("");
  const [captionSource, setCaptionSource] = useState("");
  const [loadingCaption, setLoadingCaption] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      const d = await fetchAllSnapshots();
      setData(d);
      if (!regionId && d?.regions?.[0]) setRegionId(d.regions[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regions = data?.regions || [];
  const snap = data?.snapshots?.find((s) => s.region_id === regionId);
  const format = FORMATS.find((f) => f.id === formatId);
  const mood = MOODS.find((m) => m.id === moodId);
  const m = snap?.metrics;

  const ndvi = m?.ndvi_current;
  const yieldVal = m?.yield_forecast_index;
  const soil = m?.soil_moisture_current;
  const anom = m?.ndvi_anomaly;

  const ndviS = ndviStatus(ndvi);
  const yieldS = yieldStatus(yieldVal);
  const hero = ndviS.level === "critical" || ndviS.level === "warn" ? ndviS : ndviS;
  const heroTone = toneColor(hero.level);

  const sparkPath = useMemo(() => {
    const vals = snap?.satellite?.ndvi || [];
    if (vals.length < 2) return "";
    const w = 600, h = 120;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const step = w / (vals.length - 1);
    return vals
      .map((v, i) => {
        const x = (i * step).toFixed(1);
        const y = (h - ((v - min) / (max - min || 1)) * h).toFixed(1);
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  }, [snap]);

  const onGenerate = async () => {
    setLoadingCaption(true);
    try {
      const res = await generateCaption(regionId, formatId);
      setCaption(res.caption);
      setCaptionSource(res.source);
      toast.success("Caption generiert");
    } catch {
      toast.error("Caption fehlgeschlagen");
    } finally {
      setLoadingCaption(false);
    }
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(caption || "");
    toast.success("In Zwischenablage kopiert");
  };

  const onExport = async () => {
    if (!canvasRef.current) return;
    try {
      const dataUrl = await toPng(canvasRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: mood.from,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `bodenblick-${regionId}-${formatId}-${moodId}.png`;
      a.click();
      toast.success("PNG exportiert");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  const regionLabel = regions.find((r) => r.id === regionId)?.public_label || "–";
  const isStory = formatId === "story";
  const canvasW = isStory ? 405 : 560;
  const canvasH = (canvasW * format.h) / format.w;

  // Big headline based on status
  const headline = pickHeadline(hero.level, regionLabel);

  return (
    <div className="space-y-6" data-testid="studio-root">
      <header>
        <div className="eid-label">Export · Instagram</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Content Studio</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Einfach. Lebendig. Anonymisiert. GPS-Koordinaten werden durch generische Labels
          ersetzt („Feld A", „Region Nord"). AI-Caption via Emergent Universal Key.
        </p>
      </header>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="xl:col-span-2 space-y-3">
          {/* Controls */}
          <div className="eid-card p-3 flex flex-wrap gap-3 items-center">
            <div className="flex gap-1.5 items-center" data-testid="region-selector">
              <span className="eid-label mr-1">Region</span>
              <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="studio-region-select" />
            </div>
            <div className="flex gap-1.5" data-testid="format-selector">
              <span className="eid-label self-center mr-1">Format</span>
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`format-btn-${f.id}`}
                  onClick={() => setFormatId(f.id)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors inline-flex items-center gap-1 ${
                    formatId === f.id
                      ? "bg-[#F5F5F5] text-[#0A0A0A] border-white/15"
                      : "bg-[#121212] border-white/10 hover:bg-white/5"
                  }`}
                >
                  {f.id === "story" ? <PlayCircle size={14} /> : <InstagramLogo size={14} />}
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="mood-selector">
              <span className="eid-label self-center mr-1">Stimmung</span>
              {MOODS.map((md) => (
                <button
                  key={md.id}
                  data-testid={`mood-btn-${md.id}`}
                  onClick={() => setMoodId(md.id)}
                  className={`px-2.5 py-1.5 text-xs rounded-full border transition-all inline-flex items-center gap-1.5 ${
                    moodId === md.id ? "border-white/15 shadow-sm" : "border-white/10"
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${md.from}, ${md.to})`,
                    color: md.text,
                  }}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full ring-1 ring-black/10"
                    style={{ background: md.accent }}
                  />
                  {md.label}
                </button>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="eid-card p-4 flex justify-center crosshair-bg">
            <div
              ref={canvasRef}
              data-testid="export-canvas"
              className="relative overflow-hidden shadow-lg"
              style={{
                width: canvasW,
                height: canvasH,
                background: `linear-gradient(160deg, ${mood.from} 0%, ${mood.to} 100%)`,
                color: mood.text,
                borderRadius: isStory ? 18 : 12,
              }}
            >
              {/* Decorative blobs */}
              <div
                className="absolute rounded-full opacity-40 blur-2xl"
                style={{
                  width: canvasW * 0.7,
                  height: canvasW * 0.7,
                  background: mood.accent,
                  top: -canvasW * 0.25,
                  right: -canvasW * 0.25,
                  mixBlendMode: "multiply",
                }}
              />
              <div
                className="absolute rounded-full opacity-20 blur-2xl"
                style={{
                  width: canvasW * 0.5,
                  height: canvasW * 0.5,
                  background: heroTone.dot,
                  bottom: -canvasW * 0.15,
                  left: -canvasW * 0.15,
                }}
              />

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col" style={{ padding: isStory ? 36 : 30 }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ background: mood.accent }}
                    />
                    <span
                      className="text-[11px] font-mono uppercase tracking-[0.2em]"
                      style={{ opacity: 0.7 }}
                    >
                      Bodenblick
                    </span>
                  </div>
                  <div
                    className="text-[10px] font-mono tracking-wider uppercase px-2 py-1 rounded-full"
                    style={{ background: mood.chip, color: mood.text, opacity: 0.8 }}
                  >
                    {regionLabel}
                  </div>
                </div>

                {/* Hero number */}
                <div className="mt-8 md:mt-10 flex-1 flex flex-col justify-center">
                  <div
                    className="text-[11px] font-mono uppercase tracking-[0.25em]"
                    style={{ opacity: 0.65 }}
                  >
                    NDVI · heute
                  </div>
                  <div
                    className="font-medium leading-none tracking-tight mt-2"
                    style={{
                      fontSize: isStory ? 148 : 120,
                      color: mood.text,
                      fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
                    }}
                  >
                    {ndvi != null ? ndvi.toFixed(2) : "–"}
                  </div>
                  <div
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full self-start"
                    style={{ background: mood.chip, color: mood.chipText }}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ background: heroTone.dot }}
                    />
                    <span className="text-sm font-medium">{hero.label}</span>
                  </div>

                  <div
                    className="mt-5 max-w-[85%] leading-snug"
                    style={{
                      fontSize: isStory ? 26 : 22,
                      fontFamily: "'Instrument Serif', Georgia, serif",
                      opacity: 0.92,
                    }}
                  >
                    {headline}
                  </div>
                </div>

                {/* Trend line */}
                <div className="mt-4">
                  <svg viewBox="0 0 600 120" className="w-full" style={{ height: isStory ? 90 : 70 }}>
                    <defs>
                      <linearGradient id="trendGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={mood.accent} stopOpacity="0.3" />
                        <stop offset="100%" stopColor={mood.accent} stopOpacity="1" />
                      </linearGradient>
                    </defs>
                    <path
                      d={sparkPath}
                      fill="none"
                      stroke="url(#trendGrad)"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div
                    className="text-[10px] font-mono uppercase tracking-wider flex justify-between"
                    style={{ opacity: 0.55 }}
                  >
                    <span>Letzte 30 Tage</span>
                    <span>{(snap?.fetched_at || "").slice(0, 10)}</span>
                  </div>
                </div>

                {/* Bottom chips */}
                <div className="mt-5 grid grid-cols-3 gap-2">
                  <Chip mood={mood} label="Ertrag" value={yieldVal != null ? `${Math.round(yieldVal)}` : "–"} suffix="/100" />
                  <Chip mood={mood} label="Boden" value={soil != null ? soil.toFixed(2) : "–"} suffix="m³" />
                  <Chip
                    mood={mood}
                    label="Trend"
                    value={anom != null ? `${anom >= 0 ? "+" : ""}${anom.toFixed(2)}` : "–"}
                    suffix="Δ"
                  />
                </div>
                {/* Footer watermark */}
                <div
                  className="mt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider"
                  style={{ opacity: 0.55 }}
                  data-testid="export-watermark"
                >
                  <span>bodenblick.com</span>
                  <span>Copernicus · Open-Meteo</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onExport}
              data-testid="export-png-btn"
              className="flex items-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] hover:bg-[#333] transition-colors rounded-full px-5 py-2.5 text-sm font-medium"
            >
              <DownloadSimple size={16} /> PNG für Instagram herunterladen
            </button>
            <div className="text-xs text-zinc-500 self-center font-mono">
              {format.w}×{format.h}
            </div>
          </div>
        </div>

        <aside className="eid-card p-4 space-y-3" data-testid="caption-panel">
          <h3 className="text-lg font-medium">AI Caption · Instagram</h3>
          <div className="eid-label">Region · {regionLabel}</div>
          <button
            onClick={onGenerate}
            disabled={loadingCaption}
            data-testid="generate-caption-btn"
            className="flex items-center gap-2 w-full justify-center bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 transition-colors rounded-full px-3 py-2.5 text-sm"
          >
            <Sparkle size={16} /> {loadingCaption ? "Generiere…" : "Caption generieren (Gemini 3)"}
          </button>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Hier erscheint der Instagram-Text – lebendig, verständlich, auf Deutsch…"
            data-testid="caption-textarea"
            className="w-full min-h-[220px] p-3 bg-[#0A0A0A] border border-white/10 rounded-sm text-sm font-mono text-white focus:outline-none focus:border-[#00FA9A]/40"
          />
          <div className="flex items-center justify-between">
            <div className="eid-label">
              Quelle · <span className="text-white">{captionSource || "–"}</span>
            </div>
            <button
              onClick={onCopy}
              disabled={!caption}
              data-testid="copy-caption-btn"
              className="flex items-center gap-1 text-sm px-3 py-1.5 border border-white/10 rounded-full hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <Copy size={14} /> Kopieren
            </button>
          </div>
          <div className="eid-divider" />
          <p className="text-xs text-zinc-500 leading-relaxed">
            Tipp: Bearbeite den Draft vor dem Post. GPS-Koordinaten sind bereits
            durch generische Labels ersetzt. Ideale Hashtags:
            <span className="block mt-1 font-mono text-[11px] text-white">
              #Bodenblick #Landwirtschaft #Satellitendaten #NDVI #Nachhaltigkeit
            </span>
          </p>
        </aside>
      </section>
    </div>
  );
}

function Chip({ mood, label, value, suffix }) {
  return (
    <div
      className="rounded-xl px-3 py-2"
      style={{ background: mood.chip, color: mood.chipText }}
    >
      <div
        className="text-[9px] font-mono uppercase tracking-wider"
        style={{ opacity: 0.55 }}
      >
        {label}
      </div>
      <div className="font-mono text-lg leading-tight mt-0.5">
        {value}
        {suffix && <span className="text-[10px] ml-1" style={{ opacity: 0.55 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function pickHeadline(level, label) {
  if (level === "good") return `Deine Felder stehen satt. ${label} blüht auf.`;
  if (level === "ok") return `${label} läuft stabil. Alles im grünen Bereich.`;
  if (level === "warn") return `${label} schwächelt leicht. Zeit, genauer hinzuschauen.`;
  if (level === "critical") return `${label} steht unter Stress. Jetzt handeln.`;
  return `Blick auf ${label}.`;
}
